export function loadScript(url: string, target: HTMLElement = document.head) {
    const script = document.createElement('script');
    script.setAttribute('src', url);
    target.appendChild(script);

    return new Promise((res, rej) => {
        script.onload = res;
        script.onerror = rej;
    });
}

export async function loadScriptWithError(
    url: string,
    credentials: RequestCredentials = 'same-origin',
    target: HTMLElement = document.head,
) {
    const response = await fetch(url, { method: 'HEAD', credentials });
    if (!response.ok) {
        throw new Error(`Failed to load script: ${response.status} ${response.statusText}`);
    }

    const script = document.createElement('script');
    script.setAttribute('src', url);
    target.appendChild(script);

    return new Promise((res, rej) => {
        script.onload = res;
        script.onerror = rej;
    });
}

export function loadCss(url: string, target: HTMLElement = document.head) {
    const element = document.createElement('link');
    element.setAttribute('href', url);
    element.setAttribute('rel', 'stylesheet');
    element.setAttribute('type', 'text/css');
    target.appendChild(element);
}

export function disableScrolling(el = window.document.body) {
    el.style.overflow = 'hidden';
}

export function enableScrolling(el = window.document.body) {
    el.style.overflow = '';
}

export function getScrollTop(): number {
    let bodyTop = 0;
    if (typeof window.scrollY !== 'undefined') {
        bodyTop = window.scrollY;
    } else if (typeof document.compatMode !== 'undefined' && document.compatMode !== 'BackCompat') {
        bodyTop = document.documentElement.scrollTop;
    } else if (typeof document.body !== 'undefined') {
        bodyTop = document.body.scrollTop;
    }
    return bodyTop;
}

function supportSmoothScroll() {
    return 'scrollBehavior' in document.documentElement.style;
}

function preferReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export function scrollToTop(scrollDuration: number) {
    if (preferReducedMotion() || scrollDuration === 0) {
        window.scrollTo(0, 0);
        return;
    }

    if (supportSmoothScroll()) {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
    }

    smoothScrollToPolyfill(0, 0);
}

/**
 * @see https://github.com/iamdustan/smoothscroll/blob/dbae5bd33ecf1fa4f0eaedf7df2a864a16339f5d/src/smoothscroll.js#L19
 */
const SCROLL_TIME = 468;

function smoothScrollToPolyfill(x: number, y: number) {
    const scrollLeft = window.scrollX;
    const scrollTop = window.scrollY;

    const startTime = Date.now();

    /**
     * If user touches the screen, stop scrolling
     */
    let touched = false;
    const supportTouch = 'ontouchstart' in window;
    const onTouch = () => {
        touched = true;
        window.removeEventListener('touchstart', onTouch);
    };
    if (supportTouch) {
        window.addEventListener('touchstart', onTouch, { passive: true });
    }

    const step = () => {
        const elapsed = Date.now() - startTime;
        const progress = Math.min(elapsed / SCROLL_TIME, 1);

        window.scrollTo(scrollLeft + (x - scrollLeft) * progress, scrollTop + (y - scrollTop) * progress);

        if (progress < 1 && !touched) {
            window.requestAnimationFrame(step);
        } else if (supportTouch) {
            window.removeEventListener('touchstart', onTouch);
        }
    };

    window.requestAnimationFrame(step);
}
